import { ServiceError } from '../errors'
import { readLocal, writeLocal } from '../local-store'
import { simulate } from '../mock-utils'
import type { StayRef } from '../types'
import type { GuestRequest, GuestRequestMilestone, PeticionesService } from './types'

/**
 * Mock de peticiones (contrato en docs/backend/peticiones.md). Códigos:
 *  - `con-peticiones` → las dos peticiones de PantallaAyuda.dc.html (toallas en curso, persiana resuelta)
 *  - `peticion-falla` → crear o comentar falla · `error-peticiones`, `offline-peticiones`, `lento-peticiones`
 * Las fotos no se guardan en el dispositivo (D48): solo el número, y su URL mientras dura la pestaña.
 */
export const MOCK_REQUESTS_FIELD = 'mockRequests'
const PROPERTY = 'Apartamentos Centro Histórico'

/** URL de las fotos adjuntas, en memoria. */
const photoUrls = new Map<string, string[]>()

const toUrls = (files: File[]) => (typeof URL.createObjectURL === 'function' ? files.map((f) => URL.createObjectURL(f)) : [])

export function readMockRequests(guestCode: string): GuestRequest[] {
  const stored = readLocal<GuestRequest[]>(guestCode, MOCK_REQUESTS_FIELD) ?? []
  const all = guestCode.includes('con-peticiones') ? [...stored, ...exampleRequests()] : stored
  return all.map((r) => ({ ...r, photos: photoUrls.get(r.id) ?? [] }))
}

function writeMockRequests(guestCode: string, requests: GuestRequest[]): void {
  writeLocal(
    guestCode,
    MOCK_REQUESTS_FIELD,
    requests.filter((r) => !r.id.startsWith('EJ-')).map((r) => ({ ...r, photos: [] })),
  )
}

const receivedTimeline = (now: string): GuestRequestMilestone[] => [
  { code: 'received', state: 'done', date: now, note: null },
  { code: 'progress', state: 'future', date: null, note: null },
  { code: 'resolved', state: 'future', date: null, note: null },
]

/** Peticiones de ejemplo del bundle, con fechas relativas a hoy. */
function exampleRequests(): GuestRequest[] {
  const today = (h: number, m: number) => {
    const d = new Date()
    d.setHours(h, m, 0, 0)
    return d.toISOString()
  }
  const yesterday = (h: number, m: number) => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    d.setHours(h, m, 0, 0)
    return d.toISOString()
  }
  return [
    {
      id: 'EJ-1043',
      number: '1043',
      type: 'towels_amenities',
      title: 'Toallas extra',
      text: 'Necesitamos dos toallas grandes más, por favor.',
      changeType: null,
      createdAt: today(9, 12),
      updatedAt: new Date(Date.now() - 20 * 60 * 1000).toISOString(),
      status: 'progress',
      statusNote: 'te las llevamos antes de las 13:00',
      responseWithinHours: 2,
      entryPreference: 'anytime',
      photoCount: 0,
      photos: [],
      comments: [],
      timeline: [
        { code: 'received', state: 'done', date: today(9, 12), note: 'El equipo la ha visto. Os responden en menos de 2 horas.' },
        { code: 'progress', state: 'current', date: today(9, 30), note: 'Os las llevamos antes de las 13:00.', noteAuthor: PROPERTY },
        { code: 'resolved', state: 'future', date: null, note: 'Os pediremos confirmar que ya funciona.' },
      ],
    },
    {
      id: 'EJ-1042',
      number: '1042',
      type: 'issue',
      title: 'La persiana del salón no sube',
      text: 'La persiana del salón no sube. Se queda a medias y hace ruido.',
      changeType: null,
      createdAt: yesterday(18, 40),
      updatedAt: yesterday(20, 5),
      status: 'resolved',
      statusNote: 'el técnico cambió la cinta',
      responseWithinHours: 2,
      entryPreference: 'anytime',
      photoCount: 1,
      photos: [],
      comments: [],
      timeline: [
        { code: 'received', state: 'done', date: yesterday(18, 40), note: 'El equipo la ha visto. Os responden en menos de 2 horas.' },
        { code: 'progress', state: 'done', date: yesterday(19, 5), note: 'Mañana a las 10:00 pasa Andrés, el técnico. Tarda unos 20 minutos.', noteAuthor: PROPERTY },
        { code: 'resolved', state: 'done', date: yesterday(20, 5), note: 'El técnico cambió la cinta.', noteAuthor: PROPERTY },
      ],
    },
  ]
}

/** La usa el mock de reserva al pedir un cambio a la propiedad: queda "Recibida" con plazo de 24 h (bundle B5 §5g). */
export function addMockRequest(guestCode: string, request: Pick<GuestRequest, 'text' | 'changeType'>): GuestRequest {
  const now = new Date().toISOString()
  const created: GuestRequest = {
    id: `PET-${Date.now()}`,
    number: null,
    type: 'booking_change',
    title: null,
    text: request.text,
    changeType: request.changeType,
    createdAt: now,
    updatedAt: now,
    status: 'received',
    statusNote: null,
    responseWithinHours: 24,
    entryPreference: null,
    photoCount: 0,
    photos: [],
    comments: [],
    timeline: receivedTimeline(now),
  }
  writeMockRequests(guestCode, [created, ...readMockRequests(guestCode)])
  return created
}

/** Primera frase del texto, como título que pondría el equipo (el backend lo decide). */
const firstSentence = (text: string) => {
  const s = text.split(/(?<=[.!?])\s|\n/)[0]?.trim() ?? text
  return s.replace(/[.!?]$/, '').slice(0, 80)
}

let sequence = 1044

export const peticionesMock: PeticionesService = {
  async list(stay: StayRef, signal) {
    await simulate(stay.guestCode, 'peticiones', signal)
    return readMockRequests(stay.guestCode)
  },
  async get(stay: StayRef, id, signal) {
    await simulate(stay.guestCode, 'peticiones', signal)
    const found = readMockRequests(stay.guestCode).find((r) => r.id === id)
    if (!found) throw new ServiceError('not_found', 'request not found')
    return found
  },
  async create(stay, input) {
    await simulate(stay.guestCode, 'peticiones')
    if (stay.guestCode.includes('peticion-falla')) throw new ServiceError('server', 'mock create error')
    const now = new Date().toISOString()
    const number = String(sequence++)
    const created: GuestRequest = {
      id: `PET-${Date.now()}`,
      number,
      type: input.type,
      title: firstSentence(input.text),
      text: input.text,
      changeType: null,
      createdAt: now,
      updatedAt: now,
      status: 'received',
      statusNote: null,
      responseWithinHours: 2,
      entryPreference: input.entryPreference,
      photoCount: input.photos.length,
      photos: [],
      comments: [],
      timeline: [
        { code: 'received', state: 'done', date: now, note: 'El equipo la ha visto. Os responden en menos de 2 horas.' },
        { code: 'progress', state: 'future', date: null, note: null },
        { code: 'resolved', state: 'future', date: null, note: 'Os pediremos confirmar que ya funciona.' },
      ],
    }
    const urls = toUrls(input.photos)
    if (urls.length > 0) photoUrls.set(created.id, urls)
    writeMockRequests(stay.guestCode, [created, ...readMockRequests(stay.guestCode)])
    return { ...created, photos: urls }
  },
  async addComment(stay, id, input) {
    await simulate(stay.guestCode, 'peticiones')
    if (stay.guestCode.includes('peticion-falla')) throw new ServiceError('server', 'mock comment error')
    const all = readMockRequests(stay.guestCode)
    const found = all.find((r) => r.id === id)
    if (!found) throw new ServiceError('server', 'request not found')
    const now = new Date().toISOString()
    const updated: GuestRequest = {
      ...found,
      updatedAt: now,
      photoCount: found.photoCount + input.photos.length,
      comments: [...found.comments, { id: `COM-${Date.now()}`, text: input.text, photoCount: input.photos.length, sentAt: now }],
    }
    const urls = toUrls(input.photos)
    if (urls.length > 0) photoUrls.set(id, [...(photoUrls.get(id) ?? []), ...urls])
    writeMockRequests(
      stay.guestCode,
      all.map((r) => (r.id === id ? updated : r)),
    )
    return { ...updated, photos: photoUrls.get(id) ?? [] }
  },
}
